import { Chip, HSpacer, Text, TextLink, Toolbar, VSpacer } from '@/components/DesignSystem';
import { DateRangeModal } from '@/components/shared/DateRangeModal';
import { Filter, FilterSelections } from '@/components/DesignSystem/Toolbar/interfaces';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { InfiniteScroll } from '@/components/shared/InfiniteScroll';
import { TruncatedHeaderText } from '@/components/shared/TruncatedHeaderText';
import { QueryKeys } from '@/constants/QueryKeys';
import { useProgramPeriodSelector } from '@/hooks/useProgramPeriodSelector';
import { useGetRetailerLocations } from '@/hooks/useRetailerLocations';
import { useSearch } from '@/hooks/useSearch';
import { ProgramPeriodFilterChip } from '@/pages/RetailerOverview/ProgramPeriodFilterChip';
import { ExportReportDialog } from '@/pages/Reports/ExportReportDialog';
import { RetailerApi } from '@/utilities/api/RetailerApi';
import { UserApi } from '@/utilities/api/UserApi';
import { RetailerEndpoint } from '@api/endpoints/RetailerEndpoint';
import { ApiRetailer } from '@api/interfaces';
import { Stack, Table, TableBody, TableCell, TableHead, TableRow, useTheme } from '@mui/material';
import { ReportType, UserType } from '@shared/enums';
import { SharedConfig } from '@shared/SharedConfig';
import { formatDateOnly, getAccountName } from '@shared/utilities';
import { formatCurrency, formatDate, formatNumber } from '@shared/utilities/FormatUtils';
import Check from '@mui/icons-material/Check';
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery, useQuery } from 'react-query';

interface TransactionPointsHistoryReportProps {
  onCloseExportDialog: () => void,
  retailer: ApiRetailer,
  showExportConfirmationDialog: boolean,
}

export const TransactionPointsHistoryReport = ({
  onCloseExportDialog,
  retailer,
  showExportConfirmationDialog,
}: TransactionPointsHistoryReportProps) => {
  const PAGE_SIZE = 20;
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  type DateRange = { end: Date, start: Date };
  const [filterSelections, setFilterSelections] = useState<FilterSelections>(() => new Map());
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [showDateRangeModal, setShowDateRangeModal] = useState(false);

  const tableRef = useRef<HTMLDivElement>(null);
  const [tableHeight, setTableHeight] = useState(0);

  useLayoutEffect(() => {
    const measure = () => {
      if (!tableRef.current) return;
      const { top } = tableRef.current.getBoundingClientRect();
      setTableHeight(window.innerHeight - top - 64);
    };

    measure();

    const observer = new ResizeObserver(measure);
    if (tableRef.current?.parentElement) {
      observer.observe(tableRef.current.parentElement);
    }
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  const {
    selectedProgramPeriod,
    setSelectedProgramPeriod,
    sortedPrograms,
  } = useProgramPeriodSelector({ rewardsPrograms: retailer.rewardsPrograms });

  const programMinDate = selectedProgramPeriod
    ? new Date(`${selectedProgramPeriod.startDate}T00:00:00`)
    : undefined;
  const programMaxDate = selectedProgramPeriod
    ? new Date(`${selectedProgramPeriod.endDate}T00:00:00`)
    : undefined;

  const {
    search: customerSearch,
    debouncedSearch: debouncedCustomerSearch,
    setSearch: setCustomerSearch,
  } = useSearch();

  const { retailerLocations } = useGetRetailerLocations(
    retailer.id,
    { limit: SharedConfig.maxPageLimit },
  );

  const { data: filterOptions } = useQuery(
    [QueryKeys.GET_POINTS_OVERVIEW_FILTER_OPTIONS, retailer.id],
    () => RetailerApi.getPointsOverviewFilterOptions(retailer.id),
  );

  const {
    data: customerPages,
    fetchNextPage: fetchNextCustomerPage,
    hasNextPage: hasNextCustomerPage,
    isFetching: isFetchingCustomers,
  } = useInfiniteQuery(
    [QueryKeys.LIST_USERS, retailer.id, debouncedCustomerSearch],
    async ({ pageParam = 0 }) => (
      UserApi.list({
        limit: 20,
        page: pageParam,
        retailerId: retailer.id,
        search: debouncedCustomerSearch,
        userType: [UserType.Farmer],
      })
    ),
    {
      getNextPageParam: (lastPage) =>
        lastPage.page < lastPage.lastPage ? lastPage.page + 1 : undefined,
      keepPreviousData: true,
    },
  );

  const stringifiedFilterSelections = JSON.stringify(
    Array.from(filterSelections).map(([, values]) => Array.from(values)),
  );

  const queryBody: RetailerEndpoint.GetTransactionPointsHistory.Request | null = useMemo(() => {
    if (!selectedProgramPeriod) return null;
    const farmerIds = Array.from(filterSelections.get('customer') ?? []);
    const salespersonIds = Array.from(filterSelections.get('salesperson') ?? []);
    const locationIds = Array.from(filterSelections.get('location') ?? []);
    return {
      ...(farmerIds.length ? { farmerId: farmerIds } : {}),
      ...(salespersonIds.length ? { salespersonId: salespersonIds } : {}),
      ...(locationIds.length ? { locationId: locationIds } : {}),
      ...(dateRange ? {
        dateRange: { end: formatDateOnly(dateRange.end), start: formatDateOnly(dateRange.start) },
      } : {}),
      rewardsProgramId: selectedProgramPeriod.id,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProgramPeriod, stringifiedFilterSelections, dateRange]);

  const {
    data: rows,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery(
    [QueryKeys.LIST_TRANSACTION_POINTS_HISTORY, retailer.id, queryBody],
    async ({ pageParam = 0 }) => (
      RetailerApi.listTransactionPointsHistory(retailer.id, {
        limit: PAGE_SIZE,
        page: pageParam,
        ...queryBody!,
      })
    ),
    {
      enabled: !!queryBody && !!selectedProgramPeriod,
      getNextPageParam: (lastPage, allPages) =>
        lastPage.page < lastPage.lastPage ? allPages.length : undefined,
    },
  );

  const allRows = rows?.pages?.flatMap((p) => p.data) ?? [];
  const totalCount = rows?.pages?.[0]?.total ?? 0;

  const subwalletNames: string[] = useMemo(() => {
    for (const page of rows?.pages ?? []) {
      if (page.data.length > 0) {
        return page.data[0].subwalletPoints.map((sw) => sw.subwalletName);
      }
    }
    return [];
  }, [rows]);

  const hasActiveFilters = Array.from(
    filterSelections.values(),
  ).some((s) => s.size > 0) || !!dateRange;

  const dateRangeLabel = dateRange
    ? `${formatDate(dateRange.start, i18n)} – ${formatDate(dateRange.end, i18n)}`
    : t($ => $.reports.transactionPointsHistory.dateRange);

  const filters: Filter[] = useMemo(() => [
    {
      id: 'customer',
      infiniteScrollOptions: {
        fetchMoreItems: fetchNextCustomerPage,
        hasMore: hasNextCustomerPage ?? true,
        isLoading: isFetchingCustomers,
      },
      isChipLabelVerbose: true,
      label: t($ => $.common.customer),
      onSearch: (search) => setCustomerSearch(search),
      options: (customerPages?.pages?.flatMap(({ data }) => data) ?? []).map((customer) => ({
        id: customer.id,
        label: getAccountName(customer, { appendBusinessName: true }),
      })),
      search: customerSearch,
      selectionMethod: 'multi-select',
    },
    {
      id: 'salesperson',
      isChipLabelVerbose: true,
      label: t($ => $.overview.tierProgress.salesperson),
      options: filterOptions?.salespersons ?? [],
      selectionMethod: 'multi-select',
    },
    {
      id: 'location',
      isChipLabelVerbose: true,
      label: t($ => $.overview.tierProgress.location),
      options: retailerLocations?.data.map((loc) => ({ id: loc.id, label: loc.name })) ?? [],
      selectionMethod: 'multi-select',
    },
  ], [
    customerPages,
    customerSearch,
    fetchNextCustomerPage,
    filterOptions,
    hasNextCustomerPage,
    isFetchingCustomers,
    retailerLocations,
    setCustomerSearch,
    t,
  ]);

  return (
    <Stack>
      <Stack alignItems="center" direction="row" flexWrap="wrap">
        <ProgramPeriodFilterChip
          onSelectProgram={(program) => {
            setSelectedProgramPeriod(program);
            setDateRange(undefined);
          }}
          programs={sortedPrograms}
          selectedProgram={selectedProgramPeriod}
          testID="tph-program-period"
        />
        <HSpacer size="3" />
        <Toolbar
          filterSelections={filterSelections}
          filters={filters}
          hideSearch
          onChange={({ selections }) => setFilterSelections(selections ?? new Map())}
          retainSelectionsOnFilterChange
          testID="tph-filters"
        />
        <HSpacer size="1" />
        <Chip
          label={
            <Stack alignItems="center" direction="row">
              {!!dateRange && (
                <>
                  <Check sx={{ width: '18px' }} />
                  <HSpacer size="3" />
                </>
              )}
              <Text category="label-large">{dateRangeLabel}</Text>
            </Stack>
          }
          onClick={() => setShowDateRangeModal(true)}
          size="medium"
          sx={{
            '&:hover': { backgroundColor: dateRange ? theme.palette.primaryContainer : undefined },
            backgroundColor: dateRange ? theme.palette.primaryContainer : undefined,
            borderColor: '#bdbdbd',
          }}
          testID="tph-date-range-chip"
          variant={dateRange ? 'filled' : 'outlined'}
        />
        {hasActiveFilters && (
          <>
            <HSpacer size="4" />
            <TextLink
              category="title-medium"
              onClick={() => {
                setFilterSelections(new Map());
                setDateRange(undefined);
              }}
              testID="tph-clear-all-button"
            >
              {t($ => $.common.actions.clearAll)}
            </TextLink>
          </>
        )}
      </Stack>
      <VSpacer size="3" />
      <Text category="body-small">
        {t($ => $.common.units.itemsCount, { count: totalCount })}
      </Text>
      <VSpacer size="3" />
      <div ref={tableRef} />
      <InfiniteScroll
        fetchMoreItems={fetchNextPage}
        hasMore={hasNextPage ?? true}
        height={tableHeight > 0 ? `${tableHeight}px` : undefined}
        isLoading={isLoading || isFetchingNextPage}
      >
        <Table sx={{ '& .MuiTableCell-body': { py: '4px' }, tableLayout: 'fixed' }}>
          <TableHead
            sx={{
              '& .MuiTableCell-head': { py: '4px' },
              backgroundColor: 'white',
              position: 'sticky',
              top: 0,
              zIndex: 10,
            }}
          >
            <TableRow>
              <TableCell height={56} variant="head" width={130}>
                <TruncatedHeaderText>
                  {t($ => $.reports.transactionPointsHistory.headers.customerErpId)}
                </TruncatedHeaderText>
              </TableCell>
              <TableCell height={56} variant="head" width={130}>
                <TruncatedHeaderText>
                  {t($ => $.reports.transactionPointsHistory.headers.accountGroupId)}
                </TruncatedHeaderText>
              </TableCell>
              <TableCell height={56} variant="head" width={110}>
                <TruncatedHeaderText>
                  {t($ => $.reports.transactionPointsHistory.headers.accountType)}
                </TruncatedHeaderText>
              </TableCell>
              <TableCell height={56} variant="head" width={180}>
                <TruncatedHeaderText>
                  {t($ => $.reports.transactionPointsHistory.headers.customerName)}
                </TruncatedHeaderText>
              </TableCell>
              <TableCell height={56} variant="head" width={140}>
                <TruncatedHeaderText>
                  {t($ => $.reports.transactionPointsHistory.headers.salesperson)}
                </TruncatedHeaderText>
              </TableCell>
              <TableCell height={56} variant="head" width={130}>
                <TruncatedHeaderText>
                  {t($ => $.reports.transactionPointsHistory.headers.location)}
                </TruncatedHeaderText>
              </TableCell>
              <TableCell height={56} variant="head" width={130}>
                <TruncatedHeaderText>
                  {t($ => $.reports.transactionPointsHistory.headers.orderErpId)}
                </TruncatedHeaderText>
              </TableCell>
              <TableCell height={56} variant="head" width={130}>
                <TruncatedHeaderText>
                  {t($ => $.reports.transactionPointsHistory.headers.bookingErpId)}
                </TruncatedHeaderText>
              </TableCell>
              <TableCell height={56} variant="head" width={110}>
                <TruncatedHeaderText>
                  {t($ => $.reports.transactionPointsHistory.headers.orderDate)}
                </TruncatedHeaderText>
              </TableCell>
              <TableCell height={56} variant="head" width={130}>
                <TruncatedHeaderText>
                  {t($ => $.reports.transactionPointsHistory.headers.productErpId)}
                </TruncatedHeaderText>
              </TableCell>
              <TableCell height={56} variant="head" width={180}>
                <TruncatedHeaderText>
                  {t($ => $.reports.transactionPointsHistory.headers.productName)}
                </TruncatedHeaderText>
              </TableCell>
              <TableCell height={56} variant="head" width={150}>
                <TruncatedHeaderText>
                  {t($ => $.reports.transactionPointsHistory.headers.manufacturerName)}
                </TruncatedHeaderText>
              </TableCell>
              <TableCell height={56} variant="head" width={120}>
                <TruncatedHeaderText>
                  {t($ => $.reports.transactionPointsHistory.headers.categoryName)}
                </TruncatedHeaderText>
              </TableCell>
              <TableCell height={56} variant="head" width={120}>
                <TruncatedHeaderText>
                  {t($ => $.reports.transactionPointsHistory.headers.subcategoryName)}
                </TruncatedHeaderText>
              </TableCell>
              <TableCell height={56} variant="head" width={110}>
                <TruncatedHeaderText>
                  {t($ => $.reports.transactionPointsHistory.headers.subtotal)}
                </TruncatedHeaderText>
              </TableCell>
              <TableCell height={56} variant="head" width={100}>
                <TruncatedHeaderText>
                  {t($ => $.reports.transactionPointsHistory.headers.quantity)}
                </TruncatedHeaderText>
              </TableCell>
              <TableCell height={56} variant="head" width={110}>
                <TruncatedHeaderText>
                  {t($ => $.reports.transactionPointsHistory.headers.pricePerUnit)}
                </TruncatedHeaderText>
              </TableCell>
              <TableCell height={56} variant="head" width={80}>
                <TruncatedHeaderText>
                  {t($ => $.reports.transactionPointsHistory.headers.uom)}
                </TruncatedHeaderText>
              </TableCell>
              <TableCell height={56} variant="head" width={130}>
                <TruncatedHeaderText>
                  {t($ => $.reports.transactionPointsHistory.headers.transactionType)}
                </TruncatedHeaderText>
              </TableCell>
              <TableCell height={56} variant="head" width={140}>
                <TruncatedHeaderText>
                  {t($ => $.reports.transactionPointsHistory.headers.manuallyGranted)}
                </TruncatedHeaderText>
              </TableCell>
              <TableCell height={56} variant="head" width={110}>
                <TruncatedHeaderText>
                  {t($ => $.reports.transactionPointsHistory.headers.totalPoints)}
                </TruncatedHeaderText>
              </TableCell>
              {subwalletNames.map((name) => (
                <TableCell height={56} key={name} variant="head" width={145}>
                  <TruncatedHeaderText>{`${name} points`}</TruncatedHeaderText>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!isLoading && allRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={21 + subwalletNames.length}>
                  <Text category="body-medium" sx={{ color: theme.palette.text.secondary }}>
                    {t($ => $.reports.transactionPointsHistory.noRows)}
                  </Text>
                </TableCell>
              </TableRow>
            )}
            {allRows.map((row, idx) => (
              <TableRow key={`${row.orderErpId ?? ''}-${row.productExternalId ?? ''}-${idx}`}>
                <TableCell height={56} sx={{ overflowWrap: 'anywhere' }}>
                  {row.customerErpId}
                </TableCell>
                <TableCell height={56} sx={{ overflowWrap: 'anywhere' }}>
                  {row.accountGroupId || t($ => $.common.none)}
                </TableCell>
                <TableCell height={56}>{row.accountType || t($ => $.common.none)}</TableCell>
                <TableCell height={56} sx={{ overflowWrap: 'anywhere' }}>
                  {row.customerName}
                </TableCell>
                <TableCell height={56} sx={{ overflowWrap: 'anywhere' }}>
                  {row.salespersonName || t($ => $.common.none)}
                </TableCell>
                <TableCell height={56} sx={{ overflowWrap: 'anywhere' }}>
                  {row.locationName || t($ => $.common.none)}
                </TableCell>
                <TableCell height={56} sx={{ overflowWrap: 'anywhere' }}>
                  {row.orderErpId ?? '—'}
                </TableCell>
                <TableCell height={56} sx={{ overflowWrap: 'anywhere' }}>
                  {row.bookingErpId ?? '—'}
                </TableCell>
                <TableCell height={56}>{formatDate(row.orderDate, i18n)}</TableCell>
                <TableCell height={56} sx={{ overflowWrap: 'anywhere' }}>
                  {row.productExternalId ?? '—'}
                </TableCell>
                <TableCell height={56} sx={{ overflowWrap: 'anywhere' }}>
                  {row.productName ?? '—'}
                </TableCell>
                <TableCell height={56} sx={{ overflowWrap: 'anywhere' }}>
                  {row.manufacturerName ?? '—'}
                </TableCell>
                <TableCell height={56} sx={{ overflowWrap: 'anywhere' }}>
                  {row.categoryName ?? '—'}
                </TableCell>
                <TableCell height={56} sx={{ overflowWrap: 'anywhere' }}>
                  {row.subcategoryName ?? '—'}
                </TableCell>
                <TableCell height={56}>
                  {row.subtotal != null ? formatCurrency(row.subtotal, i18n) : '—'}
                </TableCell>
                <TableCell height={56}>
                  {row.quantity != null ? formatNumber(row.quantity, i18n) : '—'}
                </TableCell>
                <TableCell height={56}>
                  {row.pricePerUnit != null ? formatCurrency(row.pricePerUnit, i18n) : '—'}
                </TableCell>
                <TableCell height={56}>{row.uom ?? '—'}</TableCell>
                <TableCell height={56}>{row.transactionType ?? '—'}</TableCell>
                <TableCell height={56}>
                  {row.isManuallyGranted ? t($ => $.common.yes) : t($ => $.common.no)}
                </TableCell>
                <TableCell height={56}>{formatNumber(row.totalPoints, i18n)}</TableCell>
                {row.subwalletPoints.map((sw) => (
                  <TableCell height={56} key={sw.subwalletId}>
                    {formatNumber(sw.amount, i18n)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </InfiniteScroll>
      {showExportConfirmationDialog && !selectedProgramPeriod && (
        <ConfirmDialog
          cancelText={t($ => $.common.actions.close)}
          onCancel={onCloseExportDialog}
          open
          showCloseButton={false}
          testID="tph-export-program-required-dialog"
        >
          {t($ => $.reports.transactionPointsHistory.exportRequiresProgramSelection)}
        </ConfirmDialog>
      )}
      {showDateRangeModal && (
        <DateRangeModal
          acceptButtonLabel={t($ => $.common.actions.apply)}
          initialEndDate={dateRange?.end ?? programMaxDate}
          initialStartDate={dateRange?.start ?? programMinDate}
          maxDate={programMaxDate}
          minDate={programMinDate}
          onClose={() => setShowDateRangeModal(false)}
          onConfirm={(start, end) => {
            setDateRange({ end, start });
            setShowDateRangeModal(false);
          }}
          open
          outOfRangeErrorMessage={programMinDate && programMaxDate
            ? t(
              $ => $.reports.transactionPointsHistory.dateRangeOutOfBounds,
              {
                endDate: formatDate(programMaxDate, i18n),
                startDate: formatDate(programMinDate, i18n),
              },
            )
            : undefined}
          testID="tph-date-range-modal"
          title={t($ => $.reports.transactionPointsHistory.dateRange)}
        />
      )}
      {showExportConfirmationDialog && !!selectedProgramPeriod && (
        <ExportReportDialog
          confirmMessage={t($ => $.reports.transactionPointsHistory.exportWarning)}
          onClose={onCloseExportDialog}
          request={{ params: queryBody!, type: ReportType.TransactionPointsHistory }}
          retailerId={retailer.id}
          testID="tph-export-confirmation-dialog"
        />
      )}
    </Stack>
  );
};
